import { useEffect, useMemo, useState } from "react";
import {
  shiftProductionDepartmentNames,
  shiftProductionLinesByDepartment,
  type ApprovalAssignment,
  type ApprovalAssignmentScope,
  type SaveApprovalAssignmentRequest,
  type ShiftProductionDepartmentName,
  type ShiftProductionLineName,
} from "../../../shared/shift-production-form/contracts";
import { Toast } from "../../shared/components/Toast";
import { useToastNotification } from "../../shared/components/useToastNotification";
import { useApprovalAssignments } from "./useApprovalAssignments";
import "./shift-form-approval.css";

const stages = [
  ["SHIFT_SUPERVISOR", "Vardiya amiri"],
  ["PRODUCTION_ENGINEER", "Üretim şefi"],
  ["PRODUCTION_MANAGER", "Üretim müdürü"],
] as const;

const stageLabels = Object.fromEntries(stages) as Record<string, string>;
const mailStatusLabels: Record<ReturnType<typeof useApprovalAssignments>["mailStatuses"][number]["status"], string> = {
  PENDING: "Kuyrukta",
  PROCESSING: "Gönderiliyor",
  SENT: "Gönderildi",
  FAILED: "Yeniden denenecek",
  DEAD: "Gönderilemedi",
  CANCELLED: "İptal edildi",
};

type CorporateUser = ReturnType<typeof useApprovalAssignments>["users"][number];

function emptyApprovers() {
  return {
    SHIFT_SUPERVISOR: null,
    PRODUCTION_ENGINEER: null,
    PRODUCTION_MANAGER: null,
  };
}

function scopeKey(scope: ApprovalAssignmentScope) {
  return `${scope.scopeType}:${scope.departmentName}:${scope.lineName ?? ""}`;
}

function AssignmentEditor({
  scope,
  assignment,
  users,
  busy,
  onSave,
  onRemove,
}: {
  scope: ApprovalAssignmentScope;
  assignment: ApprovalAssignment | null;
  users: CorporateUser[];
  busy: boolean;
  onSave: (input: SaveApprovalAssignmentRequest) => Promise<void>;
  onRemove: ((assignment: ApprovalAssignment, transferPendingForms: boolean) => Promise<void>) | null;
}) {
  const [approvers, setApprovers] = useState(() => assignment?.approvers ?? emptyApprovers());
  const [viewerUserIds, setViewerUserIds] = useState<string[]>(() => assignment?.viewerUserIds ?? []);
  const [transferPendingForms, setTransferPendingForms] = useState(false);

  useEffect(() => {
    setApprovers(assignment?.approvers ?? emptyApprovers());
    setViewerUserIds(assignment?.viewerUserIds ?? []);
  }, [assignment?.id, assignment?.version]);

  const title = scope.scopeType === "DEPARTMENT"
    ? `${scope.departmentName} bölüm varsayılanı`
    : `${scope.lineName} makine istisnası`;

  const toggleViewer = (userId: string) => {
    setViewerUserIds((current) => current.includes(userId)
      ? current.filter((item) => item !== userId)
      : [...current, userId]);
  };

  return (
    <section className="shift-assignment-editor" aria-labelledby={`assignment-${scopeKey(scope)}`}>
      <header>
        <div>
          <h3 id={`assignment-${scopeKey(scope)}`}>{title}</h3>
          <p>{scope.scopeType === "DEPARTMENT" ? "Bu bölümdeki makineler için temel onay zinciri." : "Bölüm varsayılanının yalnız bu makine için geçersiz kılınmış hali."}</p>
        </div>
        <span>{assignment ? "Tanımlı" : "Henüz tanımlı değil"}</span>
      </header>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSave({
            scope,
            approvers,
            viewerUserIds,
            expectedVersion: assignment?.version ?? 0,
            transferPendingForms,
          });
        }}
      >
        <div className="shift-assignment-stage-grid">
          {stages.map(([stage, label]) => (
            <label key={stage}>
              <span>{label}</span>
              <select
                value={approvers[stage] ?? ""}
                onChange={(event) => setApprovers((current) => ({
                  ...current,
                  [stage]: event.target.value || null,
                }))}
              >
                <option value="">Atanmadı</option>
                {users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
              </select>
            </label>
          ))}
        </div>
        <fieldset className="shift-assignment-viewers">
          <legend>Görüntüleme yetkisi</legend>
          <p>Bu kişiler formu izleyebilir; onay kararı veremez.</p>
          <div>
            {users.map((user) => (
              <label key={user.id}>
                <input
                  type="checkbox"
                  checked={viewerUserIds.includes(user.id)}
                  onChange={() => toggleViewer(user.id)}
                />
                <span>{user.name}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {assignment ? (
          <label className="shift-assignment-transfer">
            <input
              type="checkbox"
              checked={transferPendingForms}
              onChange={(event) => setTransferPendingForms(event.target.checked)}
            />
            <span>Bekleyen formları bu atamaya devret</span>
          </label>
        ) : null}
        <footer>
          {onRemove && assignment ? (
            <button
              type="button"
              className="shift-assignment-remove"
              disabled={busy}
              onClick={() => void onRemove(assignment, transferPendingForms)}
            >
              İstisnayı kaldır
            </button>
          ) : <span />}
          <button type="submit" className="shift-assignment-save" disabled={busy}>
            {assignment ? "Atamayı güncelle" : "Atamayı kaydet"}
          </button>
        </footer>
      </form>
    </section>
  );
}

export function ApprovalAssignmentsPanel() {
  const workspace = useApprovalAssignments();
  const [departmentName, setDepartmentName] = useState<ShiftProductionDepartmentName>(
    shiftProductionDepartmentNames[0],
  );
  const [lineName, setLineName] = useState<ShiftProductionLineName | "">("");
  const scope = useMemo<ApprovalAssignmentScope>(() => lineName
    ? { scopeType: "LINE", departmentName, lineName }
    : { scopeType: "DEPARTMENT", departmentName, lineName: null }, [departmentName, lineName]);
  const selectedAssignment = workspace.assignmentFor(scope);
  const defaultScope: ApprovalAssignmentScope = {
    scopeType: "DEPARTMENT",
    departmentName,
    lineName: null,
  };
  const toast = useToastNotification(
    workspace.error ?? workspace.notice,
    workspace.error ? "error" : "success",
  );
  const lineOptions = shiftProductionLinesByDepartment[departmentName];
  const assignedUserIds = useMemo(() => new Set(
    [...workspace.assignments.defaults, ...workspace.assignments.lineOverrides]
      .flatMap((assignment) => Object.values(assignment.approvers))
      .filter((userId): userId is string => Boolean(userId)),
  ), [workspace.assignments]);
  const approvers = workspace.users.filter((user) => assignedUserIds.has(user.id));

  if (workspace.phase === "loading") return <section className="shift-assignment-panel"><p>Onay atamaları yükleniyor.</p></section>;
  if (workspace.phase === "error") return <section className="shift-assignment-panel" role="alert"><p>{workspace.error}</p><button type="button" onClick={() => void workspace.reload()}>Tekrar dene</button></section>;

  return (
    <section className="shift-assignment-panel" aria-labelledby="shift-assignment-heading">
      <header className="shift-assignment-heading">
        <div>
          <h2 id="shift-assignment-heading">Vardiya formu onay atamaları</h2>
          <p>Her form önce vardiya amirine, ardından üretim şefine ve üretim müdürüne gider.</p>
        </div>
      </header>
      <div className="shift-assignment-scope">
        <label>
          <span>Bölüm</span>
          <select
            value={departmentName}
            onChange={(event) => {
              setDepartmentName(event.target.value as ShiftProductionDepartmentName);
              setLineName("");
            }}
          >
            {shiftProductionDepartmentNames.map((department) => <option key={department} value={department}>{department}</option>)}
          </select>
        </label>
        <label>
          <span>Makine istisnası</span>
          <select value={lineName} onChange={(event) => setLineName(event.target.value as ShiftProductionLineName | "")}>
            <option value="">Bölüm varsayılanı</option>
            {lineOptions.map((line) => <option key={line} value={line}>{line}</option>)}
          </select>
        </label>
      </div>
      <AssignmentEditor
        scope={scope}
        assignment={selectedAssignment}
        users={workspace.users}
        busy={workspace.busy}
        onSave={workspace.save}
        onRemove={scope.scopeType === "LINE" ? async (assignment, transferPendingForms) => {
          if (assignment.scope.scopeType !== "LINE") return;
          await workspace.remove(assignment.scope, assignment.version, transferPendingForms);
        } : null}
      />
      {scope.scopeType === "LINE" && !selectedAssignment ? (
        <p className="shift-assignment-inherited">Bu makine şu an bölüm varsayılanını kullanır. Kaydederseniz yalnız bu makine için ayrı bir zincir oluşur.</p>
      ) : null}
      {scope.scopeType === "LINE" ? (
        <p className="shift-assignment-inherited">Bölüm varsayılanı: {workspace.assignmentFor(defaultScope) ? "tanımlı" : "henüz tanımlı değil"}.</p>
      ) : null}
      <section className="shift-approver-pin-admin" aria-labelledby="shift-approver-pin-heading">
        <header>
          <div>
            <h3 id="shift-approver-pin-heading">Onaycı PIN’leri</h3>
            <p>PIN kişiye özeldir ve yalnız vardiya formu onay ekranını açar.</p>
          </div>
        </header>
        {approvers.length ? (
          <ul>
            {approvers.map((user) => (
              <li key={user.id}>
                <span><strong>{user.name}</strong><small>{user.approverPinConfigured ? "PIN tanımlı" : "PIN tanımlı değil"}</small></span>
                <button type="button" disabled={workspace.busy} onClick={() => void workspace.issuePin(user.id)}>
                  {user.approverPinConfigured ? "PIN’i yenile" : "PIN oluştur"}
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="shift-assignment-mail-empty">Önce onay zincirine kişi atayın.</p>}
        {workspace.issuedPin ? (
          <div className="shift-approver-pin-issued" role="status">
            <div><strong>{workspace.issuedPin.name}</strong><span>Bu PIN yalnız şimdi gösterilir.</span></div>
            <code>{workspace.issuedPin.pin}</code>
            <button type="button" onClick={workspace.clearIssuedPin}>Kapat</button>
          </div>
        ) : null}
      </section>
      {workspace.unassigned.total > 0 ? (
        <section className="shift-assignment-unassigned" aria-labelledby="shift-assignment-unassigned-heading">
          <h3 id="shift-assignment-unassigned-heading">Atama bekleyen formlar</h3>
          <p>Bu formlar kaydedildi; aktif onay aşaması için bir kişi atanmadığı için kuyruğa iletilmedi.</p>
          <ul>
            {workspace.unassigned.items.map((form) => (
              <li key={form.id}>
                <strong>{form.formNo}</strong>
                <span>{form.lineName ?? "Makine belirtilmedi"} · {form.productionDate ?? "Tarih belirtilmedi"}</span>
              </li>
            ))}
          </ul>
          {workspace.unassigned.total > workspace.unassigned.items.length ? <small>İlk {workspace.unassigned.items.length} form gösteriliyor.</small> : null}
        </section>
      ) : null}
      <section className="shift-assignment-mail-status" aria-labelledby="shift-assignment-mail-heading">
        <header>
          <div>
            <h3 id="shift-assignment-mail-heading">Mail durumu</h3>
            <p>Son onay bildirimleri. Teslim edilemeyen kayıtlar mevcut bildirim kuyruğunda yeniden denenir.</p>
          </div>
          <button type="button" disabled={workspace.busy} onClick={() => void workspace.reload()}>Yenile</button>
        </header>
        {workspace.mailStatuses.length ? (
          <div className="shift-assignment-mail-table-wrap">
            <table>
              <thead><tr><th>Form</th><th>Aşama</th><th>Alıcı</th><th>Durum</th></tr></thead>
              <tbody>
                {workspace.mailStatuses.map((mail) => (
                  <tr key={mail.id}>
                    <td><strong>{mail.formNo}</strong><small>Sürüm {mail.revisionNumber}</small></td>
                    <td>{stageLabels[mail.stage]}</td>
                    <td>{mail.recipientName}<small>{mail.recipient}</small></td>
                    <td><strong className={`shift-assignment-mail is-${mail.status.toLowerCase()}`}>{mailStatusLabels[mail.status]}</strong>{mail.lastError ? <small>{mail.lastError}</small> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="shift-assignment-mail-empty">Henüz onay bildirimi yok.</p>}
      </section>
      {toast.notification ? <Toast message={toast.notification.message} tone={toast.notification.tone} onDismiss={toast.dismiss} /> : null}
    </section>
  );
}
